import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import SEO, { localBusinessSchema, breadcrumbSchema } from "../components/SEO";
import { SectionLabel, Breadcrumbs, BeforeAfterSlider, CTASection } from "../components/Shared";
import { PORTFOLIO, FLAKE_COLORS, GARAGE_SIZES, IMAGES } from "../data/siteData";

const crumbs = [{ name: "Home", path: "/" }, { name: "Portfolio", path: "/portfolio" }];
const CITIES = [...new Set(PORTFOLIO.map((p) => p.city))];

const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider font-semibold text-gray-400 mb-1.5">{label}</label>
    <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="bg-white/5 border border-white/20 text-white p-3 text-sm outline-none focus:ring-2 focus:ring-[#028090] min-w-[160px]">
      <option value="" className="text-black">All</option>
      {options.map((o) => <option key={o} value={o} className="text-black">{o}</option>)}
    </select>
  </div>
);

const InlineCTA = () => (
  <div className="md:col-span-2 bg-[#028090]/10 border border-[#028090]/40 p-10 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="portfolio-inline-cta">
    <div>
      <h3 className="font-display text-2xl font-bold text-white">Your garage could be next.</h3>
      <p className="text-gray-400 text-sm mt-1">One-day install. Lifetime warranty. Free in-home estimate.</p>
    </div>
    <Link to="/free-estimate" className="bg-[#028090] text-white hover:bg-[#026c7a] transition-colors duration-300 px-8 py-4 uppercase font-bold tracking-wider whitespace-nowrap">
      Get My Free Estimate
    </Link>
  </div>
);

export default function PortfolioPage() {
  const [city, setCity] = useState("");
  const [flake, setFlake] = useState("");
  const [size, setSize] = useState("");

  const filtered = useMemo(
    () => PORTFOLIO.filter((p) => (!city || p.city === city) && (!flake || p.flake === flake) && (!size || p.size === size)),
    [city, flake, size]
  );

  const items = [];
  filtered.forEach((p, i) => {
    items.push(
      <article key={p.id} className="bg-white/5 border border-white/10 hover:border-[#028090]/50 transition-colors duration-300" data-testid={`portfolio-item-${p.id}`}>
        <BeforeAfterSlider before={p.before} after={p.after} altBefore={`bare concrete garage floor before coating ${p.city.toLowerCase()} az`} altAfter={`${p.flake.toLowerCase()} polyaspartic garage floor coating ${p.city.toLowerCase()} az`} />
        <div className="p-6">
          <h3 className="font-display text-lg font-bold text-white">{p.title}</h3>
          <p className="text-gray-400 text-sm mt-2 leading-relaxed">{p.desc}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <span className="text-xs bg-white/10 text-gray-300 px-2.5 py-1">{p.city}, AZ</span>
            <span className="text-xs bg-white/10 text-gray-300 px-2.5 py-1">{p.sqft.toLocaleString()} sq ft</span>
            <span className="text-xs bg-[#028090]/20 text-[#5fc7d4] px-2.5 py-1">{p.flake}</span>
            <span className="text-xs bg-white/10 text-gray-300 px-2.5 py-1">{p.size}</span>
          </div>
        </div>
      </article>
    );
    if ((i + 1) % 6 === 0) items.push(<InlineCTA key={`cta-${i}`} />);
  });

  return (
    <>
      <SEO
        title="Garage Floor Coating Portfolio | Before & After | Vantyx Coatings"
        description="Browse before-and-after garage floor coating projects across Scottsdale, Gilbert, Chandler, Mesa and the Phoenix Metro. Filter by flake color, garage size, and city."
        path="/portfolio"
        ogImage={IMAGES.garage1}
        schemas={[localBusinessSchema(), breadcrumbSchema(crumbs)]}
      />

      <section className="hero-gradient py-20 md:py-24 px-6 md:px-12 grain relative" data-testid="portfolio-hero">
        <div className="max-w-7xl mx-auto">
          <Breadcrumbs items={crumbs} />
          <SectionLabel>Our Work</SectionLabel>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white">
            Real Floors. <span className="text-[#028090]">Real Transformations.</span>
          </h1>
          <p className="text-gray-300 text-base md:text-lg mt-5 max-w-2xl">Drag the slider on any project to see exactly what your slab is hiding under all that grey.</p>
        </div>
      </section>

      <section className="bg-[#000000] py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-5 mb-12" data-testid="portfolio-filters">
            <FilterSelect label="City" value={city} onChange={setCity} options={CITIES} testId="portfolio-filter-city" />
            <FilterSelect label="Flake Color" value={flake} onChange={setFlake} options={FLAKE_COLORS.map((f) => f.name)} testId="portfolio-filter-flake" />
            <FilterSelect label="Garage Size" value={size} onChange={setSize} options={GARAGE_SIZES.filter((s) => s !== "1 Car")} testId="portfolio-filter-size" />
          </div>
          {filtered.length === 0 ? (
            <p className="text-gray-400" data-testid="portfolio-no-results">No projects match those filters yet — clear a filter or two.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">{items}</div>
          )}
        </div>
      </section>

      <CTASection title="Add Your Garage to This Page" />
    </>
  );
}
